import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Paths } from "src/paths";
import { useSnapshot } from "valtio";
import Loader from "src/components/common/loader/loader";
import { ScheduleService } from "@services/schedule/ScheduleService";
import { LaunchNetworkService } from "@services/engagements/launch/launchNetworkService";
import { ScheduleEntry } from "@models/ScheduleEntry.model";
import socialMediaData from "src/shared/data/socialMediaData";
import { engagementStore } from "src/modules/engagement/engagementStore";
import { workflowEvents } from "src/modules/engagement/workflow/workflowEvents";
import { EngagementService } from "@services/engagements/engagementService";
import ScheduleEntryModal from "./ScheduleEntryModal";
import MailSendRows, { type MailSendRow, loadMailSendRows } from "./MailSendRows";
import TouchScheduleRows from "./TouchScheduleRows";
import KeyDateLines, { placeKeyDates } from "./KeyDateLines";
import { type Channel } from "src/modules/engagement/components/phases/emailSms/emailSmsTypes";
import { useSequenceSchedule } from "./useSequenceSchedule";
import { AdTagChips } from "src/modules/engagement/components/adTags/AdTagChips";
import type { EngagementAdTags } from "@services/engagements/commands/cmdGetEngagementAdTags";
import {
  DAY_MS,
  barGeometry,
  campaignWindow,
  dayStart,
  flattenTree,
  formatRanges,
  summarizeRanges,
  axisTicks,
  monthBoundaries,
  typeLabel,
} from "./scheduleTree";
import "./schedule.scss";
import { useArrivalHighlight } from "@/shared/hooks/useArrivalHighlight";

const service = new ScheduleService();

const BAR_COLOR: Record<string, string> = {
  LAUNCH: "#6366f1",
  AUDIENCE: "#10b981",
  AD: "#f59e0b",
};

const networkOf = (subtype: string | null | undefined) =>
  subtype ? socialMediaData[subtype.toLowerCase()] : undefined;

/*
    The schedule as a list, not a Gantt. Every row draws its flights against the
    same campaign-wide scale, so an override visibly sits inside its parent —
    but the bars are output only. Dates change in the modal, never by dragging.
*/
export const Schedule = () => {
  const { selectedEngagement } = useSnapshot(engagementStore);
  // Opened from a failed publish: outline the flights of the network that failed.
  // A date error names no single entry, so every row for that network is a candidate.
  const highlight = useArrivalHighlight(".sched-row--highlight");
  const scheduleId = selectedEngagement?.schedule?.id;
  const engagementId = selectedEngagement?.id;

  const [entries, setEntries] = useState<ScheduleEntry[]>([]);
  const [networks, setNetworks] = useState<string[]>([]);
  // Held by id, not by row: a refetch replaces every row object, and a stale
  // one would leave the modal editing entries that no longer exist.
  const [editingId, setEditingId] = useState<number | null>(null);

  /* Campaign dates live on the Campaign page (MPN-2040). The schedule tree carries entries,
     not launches, so this lands on the Campaign step rather than one campaign -- the list
     there names them, and inventing a launch id from an entry would be a guess. */
  const navigate = useNavigate();
  const { engagementId: routeEngagementId } = useParams<{ engagementId: string }>();
  const goToCampaigns = routeEngagementId
    ? () =>
        navigate(
          `${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${routeEngagementId}/campaign`,
        )
    : undefined;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adTags, setAdTags] = useState<EngagementAdTags>({});
  /*
      Mail sends belong on the schedule for the same reason flights do: they are dated things
      this campaign will do. They were reachable only from the Direct Mail card, so the one
      screen that answers "what is going out, and when" did not know mail existed.

      They are not ScheduleEntry rows and are deliberately not modelled as any: a send is a
      batch somebody approved and costed, not a flight, and folding it into the tree would
      make every schedule consumer handle a row that has money attached to it.
  */
  const [mailRows, setMailRows] = useState<MailSendRow[]>([]);

  const loadMail = useCallback(async () => {
    if (!engagementId || engagementId < 0) return;
    setMailRows(await loadMailSendRows(engagementId));
  }, [engagementId]);

  useEffect(() => {
    void loadMail();
  }, [loadMail]);

  const load = useCallback(async () => {
    if (!scheduleId) return;
    setError(null);
    try {
      const tree = await service.getScheduleTree(scheduleId);
      setEntries(tree.entries ?? []);
    } catch {
      setError("Could not load the schedule.");
    } finally {
      setIsLoading(false);
    }
  }, [scheduleId]);

  /*
      Content Catalog tags for every ad in the engagement, in one request (MPN-1506).
      Separate from the tree so a tag failure cannot stop the schedule rendering — tags
      are context on a row, not the row itself.
  */
  useEffect(() => {
    if (!engagementId) return;
    let cancelled = false;
    new EngagementService()
      .getEngagementAdTags(engagementId)
      .then((tags) => {
        if (!cancelled) setAdTags(tags);
      })
      .catch(() => {
        if (!cancelled) setAdTags({});
      });
    return () => {
      cancelled = true;
    };
  }, [engagementId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
      Only publishers the engagement actually runs on can be overridden —
      offering the rest would let someone schedule a platform that never serves.

      Resolved from every campaign in the engagement, not from one remembered
      campaign. This used to read a "current campaign" that only the Campaign
      step wrote, so which publishers you were offered depended on where you had
      clicked, and was empty on a fresh page load. The schedule spans the whole
      engagement, so its publisher list should too.
  */
  useEffect(() => {
    if (!engagementId || engagementId < 0) return;
    let cancelled = false;

    const loadEnabledNetworks = async () => {
      const campaigns = await new EngagementService().getEngagementLaunches(engagementId);
      const service = new LaunchNetworkService();
      const configs = await Promise.all(
        campaigns.map((campaign) => service.getConfigs(campaign.id).catch(() => [])),
      );
      if (cancelled) return;
      const enabled = new Set(
        configs
          .flat()
          .filter((config: any) => config.is_enabled)
          .map((config: any) =>
            String(config.network ?? config.publisher_network ?? "").toLowerCase(),
          )
          .filter(Boolean),
      );
      setNetworks([...enabled]);
    };

    loadEnabledNetworks().catch(() => {
      if (!cancelled) setNetworks(Object.keys(socialMediaData));
    });
    return () => {
      cancelled = true;
    };
  }, [engagementId]);

  const bodyRef = useRef<HTMLDivElement>(null);
  const axisRef = useRef<HTMLDivElement>(null);
  const [trackPx, setTrackPx] = useState(0);
  const [trackLeft, setTrackLeft] = useState(0);
  const [hoverDay, setHoverDay] = useState<{ index: number; left: number } | null>(null);

  const [openChannels, setOpenChannels] = useState<Record<string, boolean>>({});

  const rows = useMemo(() => flattenTree(entries), [entries]);
  const root = entries[0] ?? null;

  /*
      This engagement's real Email & SMS sends, resolved server-side (MPN-2210).

      These used to be built-in sample sequences, which is why they were behind a permission
      gate: every user of the Schedule page saw invented sends on every engagement, and their
      January 2027 dates were folded into the chart's window below, stretching the timeline
      and squashing the real ad flights somebody came to read. Real rows need no such gate --
      an engagement with no playbook switched on simply has no rows.
  */
  const { touches: plannedTouches, keyDates: sequenceKeyDates } =
    useSequenceSchedule(engagementId);

  // Email and SMS sends, for the window below. A sequence anchored to a key date routinely
  // reaches past the last ad flight.
  const touchDates = useMemo(
    () =>
      plannedTouches
        .map((touch) => touch.send_date)
        .filter((d): d is string => !!d)
        .map((d) => {
          const [y, m, day] = d.split("-").map(Number);
          return new Date(y, m - 1, day);
        }),
    [plannedTouches],
  );

  const window = useMemo(() => {
    const base = campaignWindow(rows, root);
    // A send dated after the last flight would otherwise sit off the end of the chart, which
    // is exactly the send somebody is looking for. True of a touch as much as a mail batch.
    const dates = [...mailRows.flatMap((row) => [row.start, row.end]), ...touchDates];
    if (dates.length === 0) return base;
    const times = [base.start.getTime(), base.end.getTime(), ...dates.map((d) => d.getTime())];
    const start = new Date(Math.min(...times));
    const end = new Date(Math.max(...times));
    return {
      start,
      end,
      totalDays: Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1,
    };
  }, [rows, root, mailRows, touchDates]);
  const monthLines = useMemo(() => monthBoundaries(window), [window]);
  const placedKeyDates = useMemo(
    () =>
      placeKeyDates(
        sequenceKeyDates.map((k) => ({ id: 0, type: k.code, label: k.label, date: k.date })),
        window,
      ),
    [window, sequenceKeyDates],
  );

  // Track width drives whether a line per day is legible or just moire.
  useEffect(() => {
    const axis = axisRef.current;
    if (!axis) return;
    // offsetLeft is relative to .sched__body, which is the positioned ancestor
    // the today line and the hover cursor are both placed against.
    const measure = () => {
      setTrackPx(axis.clientWidth);
      setTrackLeft(axis.offsetLeft);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(axis);
    measure();
    return () => observer.disconnect();
  }, [rows.length]);

  const dayPx = trackPx / Math.max(window.totalDays, 1);
  const showDayLines = dayPx >= 4;
  const ticks = useMemo(() => axisTicks(window, dayPx), [window, dayPx]);

  /*
      One crosshair over the whole grid rather than per row, snapped to the day
      under the pointer — so "which day is that bar edge on" is answerable
      without counting gridlines.
  */
  const handleMove = (event: React.MouseEvent) => {
    const axis = axisRef.current;
    const body = bodyRef.current;
    if (!axis || !body) return;
    const axisRect = axis.getBoundingClientRect();
    const x = event.clientX - axisRect.left;
    if (x < 0 || x > axisRect.width) {
      setHoverDay(null);
      return;
    }
    const index = Math.min(
      window.totalDays - 1,
      Math.max(0, Math.floor((x / axisRect.width) * window.totalDays)),
    );
    setHoverDay({
      index,
      left:
        axisRect.left -
        body.getBoundingClientRect().left +
        (index / window.totalDays) * axisRect.width,
    });
  };

  const hoverDate =
    hoverDay && new Date(window.start.getTime() + hoverDay.index * DAY_MS);

  const editingRow = rows.find((r) => r.entry.id === editingId) ?? null;

  const handleSaved = () => {
    void load();
    workflowEvents.send_event("WORKFLOW_DATA_TOUCHED");
  };

  if (isLoading && rows.length === 0) return <Loader />;

  if (!scheduleId || rows.length === 0) {
    return (
      <div className="sched__empty">
        <div className="sched__empty-title">Nothing scheduled yet</div>
        <div className="sched__empty-copy">
          A schedule appears once this engagement has a campaign. Dates flow down from the
          campaign to its ad groups and ads, and any platform can be given its own.
        </div>
      </div>
    );
  }

  return (
    <>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="sched">
        <div className="sched__head">
          <i className="bi bi-calendar-range" style={{ color: "#64748b" }} />
          <span className="sched__title">Schedule</span>
          <span className="sched__chip">
            {formatRanges([{ start: window.start, end: window.end }])} · {window.totalDays} days
          </span>
          <div className="sched__head-actions">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setOpenChannels({ email: true, sms: true })}
            >
              <i className="bi bi-arrows-expand" /> Expand all
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setOpenChannels({})}
            >
              <i className="bi bi-arrows-collapse" /> Collapse all
            </button>
          </div>
        </div>

        <div className="sched__legend">
          <span className="sched__legend-item">
            <span className="sched__legend-keydate" />
            Key date
          </span>
          <span className="sched__legend-item">
            <span className="sched__legend-dot" style={{ background: "#0e7490" }} />
            Email
          </span>
          <span className="sched__legend-item">
            <span className="sched__legend-dot" style={{ background: "#6d28d9" }} />
            SMS
          </span>
          <span className="sched__legend-item">
            <span className="sched__legend-dot" style={{ background: "#f59e0b" }} />
            Blocked
          </span>
          <span className="sched__legend-note">
            Email, SMS and direct mail are read-only here.
          </span>
        </div>

        <div
          className="sched__body"
          ref={bodyRef}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverDay(null)}
        >
          <div className="sched-grid sched-grid--head">
            <div>Item</div>
            <div>Dates</div>
            <div className="sched-axis" ref={axisRef}>
              {ticks.map((tick, i) => (
                <span
                  key={i}
                  className={`sched-axis__tick${tick.isMonthStart ? " sched-axis__tick--month" : ""}`}
                  style={{ left: `${tick.leftPct}%` }}
                >
                  {tick.label}
                </span>
              ))}
              {placedKeyDates.map((keyDate) => (
                <span
                  key={`kd-${keyDate.leftPct}`}
                  className="sched-axis__keydate"
                  style={{ left: `${keyDate.leftPct}%` }}
                  title={keyDate.label}
                />
              ))}
              <span className="sched-axis__tick sched-axis__tick--end">
                {window.end.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            </div>
            <div />
          </div>

          {rows.map((row) => {
            const network = networkOf(row.entry.subtype);
            const color = network?.color ?? BAR_COLOR[row.entry.type?.toUpperCase()] ?? "#94a3b8";
            const highlighted =
              !!row.entry.subtype &&
              highlight?.toLowerCase() === `network:${row.entry.subtype}`.toLowerCase();

            /*
                The campaign's own row is read-only here (MPN-2040). Its dates are set on
                the Campaign page and every other screen already points there; leaving this
                row editable would keep two places writing the same two fields, which is
                the ambiguity that ticket exists to remove. Ad group and ad rows keep their
                own dates and stay editable.
            */
            const isCampaignRow = row.entry.type?.toUpperCase() === "LAUNCH";
            const openRow = () => {
              if (isCampaignRow) {
                goToCampaigns?.();
                return;
              }
              setEditingId(row.entry.id);
            };

            return (
              <div
                key={row.entry.id}
                className={`sched-grid sched-row sched-row--d${Math.min(row.depth, 3)}${
                  row.isOverride ? " sched-row--override" : ""
                }${highlighted ? " sched-row--highlight" : ""}${
                  isCampaignRow ? " sched-row--locked" : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={openRow}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openRow();
                  }
                }}
              >
                <div className="sched-row__name">
                  {row.isOverride && (
                    <i className="bi bi-arrow-return-right sched-row__connector" />
                  )}
                  {network ? (
                    <i className={network.icon} style={{ color: network.color }} />
                  ) : (
                    <span className={`sched-tag sched-tag--${row.entry.type?.toLowerCase()}`}>
                      {typeLabel(row.entry)}
                    </span>
                  )}
                  {isCampaignRow && (
                    <span className="sched-row__locked" title="Campaign dates are set on the Campaign page">
                      <i className="bi bi-lock-fill" /> Edit on Campaign
                    </span>
                  )}
                  <span className="sched-row__text">{row.entry.name}</span>
                  {/* An ad row carries the tags of the ad it schedules. Other rows have
                      no ad, so ad_ids is empty and this renders nothing. */}
                  {(row.entry.ad_ids ?? []).map((adId) => (
                    <AdTagChips key={adId} groups={adTags[adId]} />
                  ))}
                </div>

                <div
                  className={`sched-row__dates${row.inherits ? " sched-row__dates--inherit" : ""}${
                    row.ranges.length === 0 ? " sched-row__dates--unscheduled" : ""
                  }`}
                  title={
                    row.ranges.length === 0
                      ? "No flights — the networks will never turn this on"
                      : row.inherits
                        ? undefined
                        : formatRanges(row.ranges)
                  }
                >
                  {/* No segments means it never serves, so say that rather than
                      show an empty set of dates. */}
                  {row.ranges.length === 0
                    ? "Not scheduled"
                    : row.inherits
                      ? `follows ${typeLabel(row.parent as ScheduleEntry).toLowerCase()}`
                      : summarizeRanges(row.ranges)}
                </div>

                <div
                  className="sched-track"
                  style={
                    showDayLines
                      ? {
                          backgroundImage:
                            "linear-gradient(to right, #eef2f7 1px, transparent 1px)",
                          backgroundSize: `${100 / window.totalDays}% 100%`,
                        }
                      : undefined
                  }
                >
                  {ticks.map((tick, i) => (
                    <span
                      key={`t${i}`}
                      className="sched-track__line"
                      style={{ left: `${tick.leftPct}%` }}
                    />
                  ))}
                  {monthLines.map((left, i) => (
                    <span
                      key={`m${i}`}
                      className="sched-track__line sched-track__line--month"
                      style={{ left: `${left}%` }}
                    />
                  ))}
                  <KeyDateLines keyDates={placedKeyDates} />
                  {row.ranges.map((range, i) => {
                    const geo = barGeometry(range, window);
                    return (
                      <span
                        key={i}
                        className={`sched-bar${row.inherits ? " sched-bar--ghost" : ""}`}
                        style={{
                          left: `${geo.leftPct}%`,
                          width: `${geo.widthPct}%`,
                          background: row.inherits ? undefined : color,
                        }}
                        title={formatRanges([range])}
                      />
                    );
                  })}
                </div>

                <span className="sched-row__go" aria-hidden="true">
                  <i className="bi bi-chevron-right" />
                </span>
              </div>
            );
          })}

          <MailSendRows
            rows={mailRows}
            window={window}
            ticks={ticks}
            monthLines={monthLines}
            showDayLines={showDayLines}
            onChanged={loadMail}
          />

          <TouchScheduleRows
            engagementId={engagementId}
            touches={plannedTouches}
            window={window}
            ticks={ticks}
            monthLines={monthLines}
            showDayLines={showDayLines}
            openChannels={openChannels}
            onToggleChannel={(channel: Channel) =>
              setOpenChannels((prev) => ({ ...prev, [channel]: !prev[channel] }))
            }
          />

          <Today window={window} trackLeft={trackLeft} trackPx={trackPx} />

          {hoverDay && hoverDate && (
            <div
              className="sched-cursor"
              style={{ left: `${hoverDay.left}px`, width: `${Math.max(dayPx, 1)}px` }}
            >
              <span className="sched-cursor__label">
                {hoverDate.toLocaleDateString(undefined, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="sched__foot">
        Each channel starts as one bar from first to last send. Expand a group to see ad groups
        or each touch. Email and SMS triggers are set on the Email &amp; SMS tab.
      </div>

      {editingRow && (
        <ScheduleEntryModal
          key={editingRow.entry.id}
          entry={editingRow.entry}
          parent={editingRow.parent}
          overrides={editingRow.overrides}
          availableNetworks={networks}
          window={window}
          onClose={() => setEditingId(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

/*
    Hairline for today, drawn only while the campaign is actually running. It
    spans the whole grid rather than each track, so it reads as one line down
    the page instead of a dash per row.
*/
function Today({
  window,
  trackLeft,
  trackPx,
}: {
  window: { start: Date; end: Date; totalDays: number };
  trackLeft: number;
  trackPx: number;
}) {
  const today = dayStart(new Date());
  if (today < window.start || today > window.end) return null;
  if (trackPx === 0) return null;
  const offset = Math.round((today.getTime() - window.start.getTime()) / 86400000);
  return (
    <span
      className="sched-today"
      style={{ left: `${trackLeft + (offset / window.totalDays) * trackPx}px` }}
    />
  );
}
